import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BadgeStaleness } from "@/components/ui/badge-staleness";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  FileText,
  TrendingUp,
  DollarSign,
  Lock,
  CheckCircle,
  XCircle,
  History,
  Banknote,
  Building2,
  Loader2,
  Upload,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getFundPortfolio } from "@/lib/extraction-api";
import { supabase } from "@/integrations/supabase/client";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { chartColors } from "@/lib/theme/chartTheme";

interface AltInspectorDrawerProps {
  open: boolean;
  onClose: () => void;
  altId: string;
  quarter: string;
  assetClass?: string;
  vehicleName?: string;
}

interface HistoryRecord {
  quarter: string;
  date: string;
  overrideNav?: number;
  computedNav: number;
  calledPeriod: number;
  distributedPeriod: number;
  tvpi?: number;
  dpi?: number;
  irr?: number;
  moic?: number;
  staleDays: number;
  status: string;
  overrideReason?: string;
}

interface TransactionRecord {
  date: string;
  type: string;
  amount: number;
  currency: string;
  sourceDoc: string;
}

interface DocumentRecord {
  name: string;
  type: string;
  date: string;
  url?: string;
}

const formatCurrency = (value: number | null) => {
  if (value == null) return "—";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const formatQuarterLabel = (periodEnd: string): string => {
  const d = new Date(periodEnd);
  return `Q${Math.ceil((d.getMonth() + 1) / 3)}'${d.getFullYear().toString().slice(-2)}`;
};

export function AltInspectorDrawer({ open, onClose, altId, quarter, assetClass, vehicleName }: AltInspectorDrawerProps) {
  // Fetch alt vehicle details
  const { data: altVehicle } = useQuery({
    queryKey: ["alt-vehicle", altId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("alt_vehicles")
        .select("*")
        .eq("id", altId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!altId && open,
  });

  // Fetch period metrics
  const { data: metricsData, isLoading: metricsLoading } = useQuery({
    queryKey: ["alt-period-metrics", altId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("alt_period_metrics")
        .select("*")
        .eq("alt_id", altId)
        .order("period_end", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!altId && open,
  });

  // Fetch portfolio companies
  const { data: portfolioData, isLoading: portfolioLoading } = useQuery({
    queryKey: ["fund-portfolio", vehicleName],
    queryFn: () => getFundPortfolio(vehicleName!),
    enabled: !!vehicleName && open,
  });

  // Fetch linked documents
  const { data: linkedDocs } = useQuery({
    queryKey: ["alt-documents", altId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("alt_period_metrics")
        .select("source_doc_id, period_end, documents(id, file_name, doc_type, created_at)")
        .eq("alt_id", altId)
        .not("source_doc_id", "is", null)
        .order("period_end", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!altId && open,
  });

  // Map metrics to history records
  const history: HistoryRecord[] = useMemo(() => {
    if (!metricsData) return [];
    return metricsData.map(m => {
      const periodEnd = new Date(m.period_end);
      const staleDays = Math.floor((Date.now() - periodEnd.getTime()) / (1000 * 60 * 60 * 24));
      return {
        quarter: formatQuarterLabel(m.period_end),
        date: m.period_end,
        overrideNav: m.override_nav ? Number(m.override_nav) : undefined,
        computedNav: Number(m.computed_nav) || 0,
        calledPeriod: Number(m.called_period) || 0,
        distributedPeriod: Number(m.distributed_period) || 0,
        tvpi: m.tvpi ? Number(m.tvpi) : undefined,
        dpi: m.dpi ? Number(m.dpi) : undefined,
        irr: m.irr ? Number(m.irr) : undefined,
        moic: m.moic ? Number(m.moic) : undefined,
        staleDays,
        status: m.status || "computed",
        overrideReason: m.override_reason || undefined,
      };
    });
  }, [metricsData]);

  // Derive transactions from metrics
  const transactions: TransactionRecord[] = useMemo(() => {
    if (!metricsData) return [];
    const txns: TransactionRecord[] = [];
    const ccy = altVehicle?.currency || "USD";

    metricsData.forEach(m => {
      const calledP = Number(m.called_period) || 0;
      const distP = Number(m.distributed_period) || 0;

      if (calledP > 0) {
        txns.push({
          date: m.period_end,
          type: "Capital Call",
          amount: -calledP,
          currency: ccy,
          sourceDoc: "Capital Call Notice",
        });
      }
      if (distP > 0) {
        txns.push({
          date: m.period_end,
          type: "Distribution",
          amount: distP,
          currency: ccy,
          sourceDoc: "Distribution Notice",
        });
      }
    });

    return txns.sort((a, b) => b.date.localeCompare(a.date));
  }, [metricsData, altVehicle]);

  // Map linked docs to document records
  const documents: DocumentRecord[] = useMemo(() => {
    if (!linkedDocs) return [];
    const seen = new Set<string>();
    return linkedDocs
      .filter(d => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const doc = (d as any).documents;
        if (!doc || seen.has(doc.id)) return false;
        seen.add(doc.id);
        return true;
      })
      .map(d => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const doc = (d as any).documents;
        return {
          name: doc.file_name || "Unknown Document",
          type: doc.doc_type || "Statement",
          date: doc.created_at ? new Date(doc.created_at).toLocaleDateString() : d.period_end,
        };
      });
  }, [linkedDocs]);

  // Portfolio companies data
  const investments = portfolioData?.investments ?? [];
  const latestDate = investments.length > 0
    ? investments.reduce((latest, inv) => inv.statement_date > latest ? inv.statement_date : latest, investments[0].statement_date)
    : null;
  const totalCost = investments.reduce((sum, inv) => sum + (inv.cost_basis ?? 0), 0);
  const totalFV = investments.reduce((sum, inv) => sum + (inv.fair_value ?? 0), 0);
  const totalGL = investments.reduce((sum, inv) => sum + (inv.unrealized_gain_loss ?? 0), 0);

  // Overview computed values
  const latestMetric = history.length > 0 ? history[0] : null;
  const currentNav = latestMetric ? (latestMetric.overrideNav || latestMetric.computedNav) : 0;
  const calledCum = metricsData?.[0] ? Number(metricsData[0].called_cum) || 0 : 0;
  const unrealizedGL = currentNav - calledCum;
  const returnPct = calledCum > 0 ? ((unrealizedGL / calledCum) * 100) : 0;
  const commitment = altVehicle ? Number(altVehicle.commitment) || 0 : 0;
  const vintage = altVehicle?.vintage;

  // NAV trend chart data
  const navTrendData = useMemo(() => {
    if (!metricsData || metricsData.length === 0) return [];
    return [...metricsData]
      .reverse()
      .map(m => ({
        quarter: formatQuarterLabel(m.period_end),
        nav: (Number(m.override_nav) || Number(m.computed_nav) || 0) / 1_000_000,
      }));
  }, [metricsData]);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-[900px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-2xl">
            {vehicleName || "Alternative Investment Inspector"}
          </SheetTitle>
          <SheetDescription>
            {altVehicle?.manager_name && `${altVehicle.manager_name} • `}
            {assetClass || "Private Equity"}
            {altVehicle?.status && altVehicle.status !== "Active" && ` • ${altVehicle.status}`}
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio Cos</TabsTrigger>
            <TabsTrigger value="marks">Marks</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="override">Override</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Position Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Market Value (NAV)</p>
                    <p className="text-2xl font-bold">{formatCurrency(currentNav)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Cost Basis (Called)</p>
                    <p className="text-2xl font-semibold">{formatCurrency(calledCum)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Unrealized G/L</p>
                    <p className={`text-2xl font-bold ${unrealizedGL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(unrealizedGL)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(2)}% on cost
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Currency</p>
                    <p className="text-lg font-medium">{altVehicle?.currency || "USD"}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mt-6 pt-6 border-t">
                  {commitment > 0 && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Commitment</p>
                      <p className="text-lg font-semibold">{formatCurrency(commitment)}</p>
                    </div>
                  )}
                  {vintage && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Vintage</p>
                      <p className="text-lg font-semibold">{vintage}</p>
                    </div>
                  )}
                  {latestMetric?.tvpi != null && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">TVPI</p>
                      <p className="text-lg font-semibold">{latestMetric.tvpi.toFixed(2)}x</p>
                    </div>
                  )}
                  {latestMetric?.dpi != null && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">DPI</p>
                      <p className="text-lg font-semibold">{latestMetric.dpi.toFixed(2)}x</p>
                    </div>
                  )}
                  {latestMetric?.irr != null && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">IRR</p>
                      <p className="text-lg font-semibold">{latestMetric.irr.toFixed(1)}%</p>
                    </div>
                  )}
                  {latestMetric && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Data Staleness</p>
                      <BadgeStaleness days={latestMetric.staleDays} />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {navTrendData.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>NAV Trend</CardTitle>
                  <CardDescription>Quarterly valuation history</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={navTrendData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="quarter" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                      <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(value) => `$${value.toFixed(0)}M`} />
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '6px' }} />
                      <Line type="monotone" dataKey="nav" stroke={chartColors[0]} strokeWidth={2} dot={{ fill: chartColors[0] }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {navTrendData.length <= 1 && currentNav === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">NAV trend will appear once quarterly statements are processed.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* PORTFOLIO COMPANIES TAB */}
          <TabsContent value="portfolio" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Portfolio Companies</CardTitle>
                    <CardDescription>Schedule of Investments — underlying company positions</CardDescription>
                  </div>
                  {latestDate && <Badge variant="outline">As of {latestDate}</Badge>}
                </div>
              </CardHeader>
              <CardContent>
                {portfolioLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : investments.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Company</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Sector</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Fair Value</TableHead>
                          <TableHead className="text-right">G/L</TableHead>
                          <TableHead className="text-right">Own %</TableHead>
                          <TableHead>Stage</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {investments.map((inv, idx) => (
                          <TableRow key={inv.id || idx}>
                            <TableCell className="font-medium">{inv.investment_name}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{inv.investment_type || "—"}</TableCell>
                            <TableCell>{inv.industry_sector ? <Badge variant="outline" className="text-xs">{inv.industry_sector}</Badge> : "—"}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{formatCurrency(inv.cost_basis)}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{formatCurrency(inv.fair_value)}</TableCell>
                            <TableCell className={`text-right font-mono text-sm ${(inv.unrealized_gain_loss ?? 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                              {inv.unrealized_gain_loss != null ? `${inv.unrealized_gain_loss >= 0 ? "+" : ""}${formatCurrency(inv.unrealized_gain_loss)}` : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">{inv.ownership_percentage != null ? `${(inv.ownership_percentage * 100).toFixed(1)}%` : "—"}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{inv.investment_stage || "—"}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell>Total ({investments.length} companies)</TableCell>
                          <TableCell /><TableCell />
                          <TableCell className="text-right font-mono text-sm">{formatCurrency(totalCost)}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatCurrency(totalFV)}</TableCell>
                          <TableCell className={`text-right font-mono text-sm ${totalGL >= 0 ? "text-green-600" : "text-red-600"}`}>{totalGL >= 0 ? "+" : ""}{formatCurrency(totalGL)}</TableCell>
                          <TableCell /><TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Portfolio Data</h3>
                    <p className="text-muted-foreground max-w-sm">Upload quarterly financial statements to see portfolio company positions.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MARKS & HISTORY TAB */}
          <TabsContent value="marks" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Valuation History</CardTitle>
                    <CardDescription>Quarter-by-quarter marks, overrides, and performance</CardDescription>
                  </div>
                  <Button variant="outline" size="sm"><TrendingUp className="h-4 w-4" />Chart View</Button>
                </div>
              </CardHeader>
              <CardContent>
                {metricsLoading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
                ) : history.length > 0 ? (
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Quarter</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">NAV</TableHead>
                          <TableHead className="text-right">Called (P)</TableHead>
                          <TableHead className="text-right">Dist (P)</TableHead>
                          <TableHead className="text-right">TVPI</TableHead>
                          <TableHead className="text-right">DPI</TableHead>
                          <TableHead className="text-right">IRR</TableHead>
                          <TableHead>Staleness</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {history.map((record, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{record.quarter}</TableCell>
                            <TableCell className="text-sm">{record.date}</TableCell>
                            <TableCell className="text-right">
                              <div className="space-y-1">
                                <div className="font-semibold">{formatCurrency(record.overrideNav || record.computedNav)}</div>
                                {record.overrideNav && <div className="text-xs text-muted-foreground">Base: {formatCurrency(record.computedNav)}</div>}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(record.calledPeriod)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(record.distributedPeriod)}</TableCell>
                            <TableCell className="text-right font-medium">{record.tvpi != null ? `${record.tvpi.toFixed(2)}x` : "—"}</TableCell>
                            <TableCell className="text-right">{record.dpi != null ? `${record.dpi.toFixed(2)}x` : "—"}</TableCell>
                            <TableCell className="text-right">{record.irr != null ? `${record.irr.toFixed(1)}%` : "—"}</TableCell>
                            <TableCell><BadgeStaleness days={record.staleDays} /></TableCell>
                            <TableCell>
                              <Badge variant={record.status === "locked" ? "outline" : record.status === "approved" ? "secondary" : "default"}>
                                {record.status === "locked" && <Lock className="h-3 w-3 mr-1" />}{record.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <History className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Valuation History</h3>
                    <p className="text-muted-foreground max-w-sm">Valuation history will appear here once GP statements are uploaded and processed.</p>
                  </div>
                )}

                {history.some(m => m.overrideReason) && (
                  <Card className="mt-4 border-blue-200 bg-blue-50/50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div className="flex-1 space-y-1">
                          <p className="text-sm font-medium">Recent Override</p>
                          <p className="text-sm text-muted-foreground">{history.find(m => m.overrideReason)?.overrideReason}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TRANSACTIONS TAB */}
          <TabsContent value="transactions" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Transaction History</CardTitle>
                    <CardDescription>Capital calls, distributions, and cash flows</CardDescription>
                  </div>
                  <Button variant="outline" size="sm"><Upload className="h-4 w-4" />Record Transaction</Button>
                </div>
              </CardHeader>
              <CardContent>
                {transactions.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Currency</TableHead>
                          <TableHead>Source</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((txn, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{txn.date}</TableCell>
                            <TableCell>
                              <Badge variant={txn.type.includes('Distribution') ? "default" : "outline"}>
                                {txn.amount > 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                                {txn.type}
                              </Badge>
                            </TableCell>
                            <TableCell className={`text-right ${txn.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              <div className="flex items-center justify-end gap-1"><DollarSign className="h-4 w-4" />{formatCurrency(Math.abs(txn.amount))}</div>
                            </TableCell>
                            <TableCell>{txn.currency}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{txn.sourceDoc || "Manual entry"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Banknote className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Transactions</h3>
                    <p className="text-muted-foreground max-w-sm">Transaction history will appear here once capital call and distribution notices are processed.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* OVERRIDE TAB */}
          <TabsContent value="override" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Propose Override</CardTitle>
                <CardDescription>Adjust NAV for this period with justification and source documentation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="override-nav">Override NAV</Label>
                    <Input id="override-nav" type="number" placeholder="Enter new NAV" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="override-asof">As-of Date</Label>
                    <Input id="override-asof" type="date" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="override-reason">Reason <span className="text-destructive">*</span></Label>
                  <Textarea id="override-reason" placeholder="Explain why this override is needed..." rows={3} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="override-doc">Supporting Document</Label>
                  <Input id="override-doc" type="file" />
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1">Propose Override</Button>
                  <Button variant="outline" className="flex-1">Cancel</Button>
                </div>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium">Manager Approval Required</p>
                    <p className="text-sm text-muted-foreground">Proposed overrides must be reviewed and approved by a Manager before they affect position metrics and pack generation.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Documents</CardTitle>
                    <CardDescription>Statements, reports, and supporting materials</CardDescription>
                  </div>
                  <Button variant="outline" size="sm"><Upload className="h-4 w-4" />Upload</Button>
                </div>
              </CardHeader>
              <CardContent>
                {documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{doc.name}</p>
                            <p className="text-xs text-muted-foreground">{doc.type} • {doc.date}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">Open</Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Documents</h3>
                    <p className="text-muted-foreground max-w-sm">Uploaded GP statements and supporting materials will appear here.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
